import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineShieldCheck,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

// Synchronous cache loading function
const getCachedPpeSafetyData = () => {
  try {
    const cachedPpeItems = sessionStorage.getItem('ppe_safety_data');
    
    return {
      ppeItems: cachedPpeItems ? JSON.parse(cachedPpeItems) : [],
      hasAll: !!cachedPpeItems
    };
  } catch (e) {
    console.error('Error parsing cached PPE Safety data:', e);
    return { ppeItems: [], hasAll: false };
  }
};

const PpeSafetyPage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedPpeSafetyData();
  const [ppeItems, setPpeItems] = useState(cachedInitialData.ppeItems);
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    ppeName: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadPpeItems(!hasCachedData);
    } else {
      setError('Please login to view PPE items');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadPpeItems = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const data = await masterDataService.getPpeSafety(tenantId);
      setPpeItems(data || []);
      
      // Cache the data
      sessionStorage.setItem('ppe_safety_data', JSON.stringify(data || []));
    } catch (err) {
      setError('Failed to load PPE items. Please try again.');
      console.error('Error loading PPE items:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setPpeItems([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredItems = ppeItems.filter((item) =>
    item.ppeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      ppeName: '',
      description: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ppeName: item.ppeName || '',
      description: item.description || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deletePpeSafety(id);
      await loadPpeItems();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete PPE item. Please try again.');
      console.error('Error deleting PPE item:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.ppeName.trim()) {
      errors.ppeName = 'PPE name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const ppeData = {
        tenantId: tenantId,
        ppeName: formData.ppeName.trim(),
        description: formData.description.trim() || null,
      };

      if (editingItem) {
        ppeData.ppeId = editingItem.ppeId;
        await masterDataService.updatePpeSafety(editingItem.ppeId, ppeData);
      } else {
        await masterDataService.createPpeSafety(ppeData);
      }

      await loadPpeItems();
      setShowModal(false);
      setFormData({ ppeName: '', description: '' });
      setEditingItem(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save PPE item. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ ppeName: '', description: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading PPE items..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>PPE (Safety)</h1>
          <p>Manage Personal Protective Equipment for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add PPE
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search PPE items..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <HiOutlineShieldCheck className="empty-icon" />
            <h3>No PPE items found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first PPE item'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add PPE
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>PPE Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.ppeId}>
                  <td>{item.ppeId}</td>
                  <td className="name-cell">{item.ppeName}</td>
                  <td>{item.description || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(item)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(item)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingItem ? 'Edit PPE Item' : 'Add New PPE Item'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="ppe-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingItem
                  ? 'Updating...'
                  : 'Creating...'
                : editingItem
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="ppe-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="ppeName">
                PPE Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="ppeName"
                value={formData.ppeName}
                onChange={(e) =>
                  setFormData({ ...formData, ppeName: e.target.value })
                }
                className={formErrors.ppeName ? 'error' : ''}
                placeholder="Enter PPE name"
              />
              {formErrors.ppeName && (
                <span className="field-error">{formErrors.ppeName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description (optional)"
                rows="4"
              />
            </div>
          </form>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.ppeId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.ppeName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default PpeSafetyPage;
